import type { LeadState, StepId, InsuranceId, BotItem, Audience } from '../types'

export const insuranceLabels: Record<InsuranceId, string> = {
  bil: 'Bil',
  indbo: 'Indbo',
  ulykke: 'Ulykke',
  hus: 'Hus',
  hund: 'Hund',
  andre: 'Andre',
  ingen: 'Ingen forsikring endnu',
}

export function formatInsurances(ids: InsuranceId[] | undefined): string {
  if (!ids || ids.length === 0) return ''
  return ids.map((id) => insuranceLabels[id] ?? id).join(', ')
}

/**
 * Estimate annual savings range. Audience-aware:
 *   - unge: aggressive (ung-bilist præmie er typisk meget høj, der er stor
 *     diskount-headroom — ung chauffør får +bonus til savingsRate).
 *   - aeldre: lavere base (~12%), uden ung-bonus, så reframen matcher
 *     `public/calculator-aeldre/index.html` der bruger ~10% base.
 */
export function calculateSavings(state: LeadState, audience: Audience): { min: number; max: number } {
  const monthlyNow = state.currentPriceVal ?? 1000
  const ageNum = parseInt(state.age ?? '25', 10) || 25
  const numIns = state.insurances?.length ?? 1

  let savingsRate: number
  if (audience === 'aeldre') {
    savingsRate = 0.12
    if (numIns >= 3) savingsRate += 0.02
  } else {
    savingsRate = 0.18
    if (ageNum < 23) savingsRate = 0.25
    else if (ageNum < 26) savingsRate = 0.22
    if (numIns >= 3) savingsRate += 0.03
  }

  const yearlyNow = monthlyNow * 12
  const minSave = Math.round((yearlyNow * (savingsRate - 0.05)) / 100) * 100
  const maxSave = Math.round((yearlyNow * (savingsRate + 0.07)) / 100) * 100
  return {
    min: Math.max(1000, minSave),
    max: Math.max(2500, maxSave),
  }
}

export interface StepDef {
  getBotMessages: (state: LeadState, audience: Audience) => BotItem[]
  getUserMessage: (state: LeadState) => string
}

const claimsLabels: Record<string, string> = {
  '0': 'Ingen skader',
  '1': '1 skade',
  '2': '2 skader',
  '3+': '3 eller flere',
}

export const stepDefinitions: Record<Exclude<StepId, 'thankyou'>, StepDef> = {
  welcome: {
    getBotMessages: (_s, audience) => {
      const hook = audience === 'aeldre'
        ? 'De fleste har ikke tjekket deres forsikring i årevis — og ender med at betale alt for meget uden at vide det.'
        : 'Unge bilister betaler op til 4.000 kr. for meget om året - det kan vi forhåbentlig hjælpe dig med at undgå.'
      return [
        'Hej! Jeg hedder Fiona 👋 Jeg er din digitale forsikringsrådgiver fra Fælles Forsikring',
        hook,
        'Skal vi tjekke det på 60 sekunder?',
        // Inline quick-reply CTA — rendereres som knap inde i chat-tråden
        // (ikke i input-docken). MessageList kalder onCta-handler ved klik;
        // Widget mapper det til submitStep({}) for at avancere til insurances.
        { kind: 'cta', label: 'Start' },
      ]
    },
    // Brugeren har ikke skrevet noget eksplicit — vi viser ikke en user-bobble
    // efter Start-klikket (rendereren springer over når dette returnerer '').
    getUserMessage: () => '',
  },
  insurances: {
    getBotMessages: () => [
      'Super! 🙌',
      'Hvilke forsikringer har du i dag?',
      '💡 PS: Samler du indbo + 2 andre forsikringer hos os, får du 15% samlerabat.',
    ],
    getUserMessage: (s) => formatInsurances(s.insurances),
  },
  postnr: {
    getBotMessages: (s) => {
      const sel = formatInsurances(s.insurances)
      return [sel ? `${sel} – super! 👍` : 'Perfekt 🚀', 'Hvilket postnummer bor du i?']
    },
    getUserMessage: (s) => (s.by ? `${s.postnr} · ${s.by}` : (s.postnr ?? '')),
  },
  age: {
    getBotMessages: (s) => [`${s.by ?? 'Noteret'} 📍`, 'Hvor gammel er du?'],
    getUserMessage: (s) => `${s.age} år`,
  },
  currentPrice: {
    getBotMessages: (s, audience) => {
      let resp: string
      if (audience === 'aeldre') {
        resp = 'Tak — så ved vi cirka hvor du står 👍'
      } else {
        const ageNum = parseInt(s.age ?? '', 10)
        resp = 'Noteret 👍'
        if (s.age === '30+') resp = 'Super 👌'
        else if (ageNum < 23) resp = 'Ung chauffør! Vi får det til at fungere 💪'
        else if (ageNum < 26) resp = 'Perfekt alder for gode priser 👌'
      }
      return [resp, 'Hvad betaler du cirka nu om måneden for dine forsikringer?']
    },
    getUserMessage: (s) => s.currentPrice ?? '',
  },
  km: {
    getBotMessages: () => ['Tak 🙌', 'Hvor meget kører du om året?'],
    getUserMessage: (s) => s.kmYear ?? '',
  },
  claims: {
    getBotMessages: () => ['Næsten færdig 🎉', 'Har du haft skader de sidste 3 år?'],
    getUserMessage: (s) => claimsLabels[s.claims ?? ''] ?? s.claims ?? '',
  },
  phone: {
    getBotMessages: (s, audience) => {
      const hasNone = (s.insurances ?? []).includes('ingen')
      if (hasNone) {
        return [
          'Det er helt fint! 🙌',
          'Skal vi regne en pris til dig? 📞',
        ]
      }
      const sav = calculateSavings(s, audience)
      return [
        'Ud fra dine svar — og andre vi har hjulpet i din alder og dit område 👇',
        { kind: 'savings', min: sav.min, max: sav.max },
        'Skal en rådgiver ringe dig op med dit personlige tilbud?',
      ]
    },
    // Vis navn + telefon på to linjer i user-bobblen. Linjeskift renderes via
    // `white-space: pre-line` på .msg-user-bubble. Filtrerer "-" fra lastName
    // (placeholder når brugeren kun har skrevet ét navn) så vi ikke får
    // "Viggo -" stående pænt i bubblen.
    getUserMessage: (s) => {
      const namePart = [s.firstName, s.lastName]
        .filter((p) => p && p !== '-')
        .join(' ')
      const lines: string[] = []
      if (namePart) lines.push(`Navn: ${namePart}`)
      if (s.phone) lines.push(`Telefonnummer: ${s.phone}`)
      return lines.join('\n')
    },
  },
}
